import React from 'react';
import parse, { Element, DOMNode, domToReact } from 'html-react-parser';
import { AllieTable } from '../../../components/Table/AllieTable';

export type MarkdownParseOptions = Parameters<typeof parse>[1];

type ParseOptions = MarkdownParseOptions | Parameters<typeof domToReact>[1];

const isTag = (node: DOMNode): node is Element =>
  node.type === 'tag' && 'name' in node;

const cellChildren = (cell: Element, options: ParseOptions) =>
  domToReact(cell.children as DOMNode[], options);

const rowsFrom = (parent: Element, options: ParseOptions) =>
  parent.children
    .filter(isTag)
    .filter((child) => child.name === 'tr')
    .map((row) =>
      row.children
        .filter(isTag)
        .filter((cell) => cell.name === 'th' || cell.name === 'td')
        .map((cell) => cellChildren(cell, options)),
    );

export function MarkdownHtmlTable({
  tableNode,
  options,
}: {
  tableNode: Element;
  options: ParseOptions;
}) {
  const sections = tableNode.children.filter(isTag);
  let headerCells: ReturnType<typeof cellChildren>[] = [];
  let bodyRows: ReturnType<typeof cellChildren>[][] = [];

  sections.forEach((section) => {
    if (section.name === 'thead') {
      const rows = rowsFrom(section, options);
      headerCells = rows[0] ?? [];
      return;
    }
    if (section.name === 'tbody') {
      bodyRows = rowsFrom(section, options);
      return;
    }
    if (section.name === 'tr') {
      const cells = section.children
        .filter(isTag)
        .filter((cell) => cell.name === 'th' || cell.name === 'td')
        .map((cell) => cellChildren(cell, options));
      if (section.children.filter(isTag).some((cell) => cell.name === 'th')) {
        headerCells = cells;
        return;
      }
      bodyRows = [...bodyRows, cells];
    }
  });

  return (
    <AllieTable variant="card" rowStyle="zebra" interaction="hover">
      {headerCells.length > 0 ? (
        <AllieTable.Header>
          <AllieTable.Row>
            {headerCells.map((cell, index) => (
              <AllieTable.Head key={index} align="left">
                {cell}
              </AllieTable.Head>
            ))}
          </AllieTable.Row>
        </AllieTable.Header>
      ) : null}
      <AllieTable.Body>
        {bodyRows.map((row, rowIndex) => (
          <AllieTable.Row key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <AllieTable.Cell key={cellIndex} align="left">
                {cell}
              </AllieTable.Cell>
            ))}
          </AllieTable.Row>
        ))}
      </AllieTable.Body>
    </AllieTable>
  );
}
